import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { auth } from '../../config/firebase';
import { Link } from 'react-router-dom';
import { useBillingPage } from '../../hooks/useBillingPage';
import { useAccess } from '../../hooks/useAccess';

export const TrialPeriodNotificationBar: React.FC = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(true);
  const [daysLeft, setDaysLeft] = useState<number | null>(null);
  const { data: billingData } = useBillingPage();
  const { data: accessData } = useAccess();
  const subscriptionStatus = (billingData?.currentSubscription?.status || '').toLowerCase();
  const hasPaidSubscription = Boolean(
    billingData?.hasSubscription &&
    (billingData?.currentSubscription?.isActive || subscriptionStatus === 'active' || subscriptionStatus === 'trialing') &&
    (
      !billingData?.currentSubscription?.currentPlan ||
      (billingData.currentSubscription.currentPlan?.productName || '').toLowerCase() !== 'free'
    )
  );
  // Invited team members inherit their organisation's subscription via /access.
  // If the org is entitled (active/trialing), hide the trial banner even when
  // the user's personal billing record is empty.
  const isOrgEntitled = Boolean(accessData?.access?.isEntitled);

  useEffect(() => {
    const user = auth.currentUser;
    const creation = user?.metadata?.creationTime;
    if (!creation) {
      setDaysLeft(null);
      return;
    }
    const createdAt = new Date(creation);
    if (Number.isNaN(createdAt.getTime())) {
      setDaysLeft(null);
      return;
    }
    const now = new Date();
    const diffMs = now.getTime() - createdAt.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    // Inclusive countdown to match requested example (yesterday -> 7 days left)
    const remaining = Math.max(0, Math.min(7, 7 - Math.floor(diffDays - 1)));
    setDaysLeft(remaining);
  }, []);

  const trialEnded = useMemo(() => typeof daysLeft === 'number' && daysLeft <= 0, [daysLeft]);

  // Static beta end date
  const betaEndDate = useMemo(() => {
    const d = new Date('2025-08-22T00:00:00Z');
    const day = d.getUTCDate();
    const month = d.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
    const suffix = (n: number) => {
      if (n % 10 === 1 && n % 100 !== 11) return 'st';
      if (n % 10 === 2 && n % 100 !== 12) return 'nd';
      if (n % 10 === 3 && n % 100 !== 13) return 'rd';
      return 'th';
    };
    return `${day}${suffix(day)} of ${month}`;
  }, []);

  // Don't render if dismissed, the user has a paid plan, or they are
  // covered by their organisation's active subscription (invited members).
  if (!isVisible || hasPaidSubscription || isOrgEntitled) return null;

  return (
    <div className="text-white px-4 py-3 sticky top-0 z-50" style={{ backgroundColor: 'rgb(235 76 22)' }}>
      <div className="max-w-7xl mx-auto relative">
        <div className="flex items-center justify-center gap-2">
          {trialEnded ? (
            <>
              <span className="text-base">⚠️</span>
              <p className="text-sm font-medium text-center">
                {t('beta.trial.overPrefix', 'Your trial period is over.')} {' '}
                <Link to="/billing" className="underline font-semibold text-white/95 hover:text-white">
                  {t('beta.trial.subscribeNow', 'Subscribe now')}
                </Link>
              </p>
            </>
          ) : (
            <>
              <span className="text-base">⚠️</span>
              <p className="text-sm font-medium text-center">
                {t('beta.trial.leftPrefix', 'Only')} {daysLeft ?? 7} {t('beta.trial.leftDays', 'days left in your trial period.')}{' '}
                <Link to="/billing" className="underline font-semibold text-white/95 hover:text-white">
                  {t('beta.trial.subscribeNow', 'Subscribe now')}
                </Link>
              </p>
            </>
          )}
        </div>
        
        <button
          onClick={() => setIsVisible(false)}
          className="absolute right-0 top-1/2 -translate-y-1/2 p-1 hover:bg-white/20 rounded-full transition-colors"
          aria-label={t('common.close', 'Close')}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}; 